import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    ApplicationFacade,
    CustomerAccount,
    CustomerAccountsFacade,
    PurchaseService,
    SessionService,
    TicketsService
} from '@ridango/components';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { Area } from '@ridango/components/lib/purchase/purchase.model';
import Env from '@ridango/core/env';
import { EnhancedFareEngineTicket } from '@ridango/components/lib/account-tickets-ngrx/models/ticket.model';

@Component({
    selector: 'gl-purchase-v2-ticket-areas',
    templateUrl: './areas.component.html'
})
export class AreasComponent extends BaseComponent implements OnInit, OnDestroy {
    @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();
    @Input() selectedAreaId: number | string;
    @Input() areas: Area[];

    currentLanguage = this.translate.currentLang;
    recentTickets: EnhancedFareEngineTicket[];
    public readonly assetsPath$ = this.applicationFacade.assetsPath$;

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private translate: TranslateService,
        private purchaseService: PurchaseService,
        private ticketService: TicketsService,
        private customerAccountsFacade: CustomerAccountsFacade,
        private sessionService: SessionService
    ) {
        super();
    }

    ngOnInit(): void {
        this.purchaseService.setPurchaseDataLoading(false);
        this.initSubscriptions();
    }

    async selectArea(
        areaId: number | string,
        ticket?: EnhancedFareEngineTicket & {
            geography: { zones: Array<{ code: string }> };
            timeIntervals: Array<{ code: string }>;
        }
    ) {
        const urlSegments = [...this.activatedRoute.snapshot.url.map((segment) => segment.path)];

        if (this.selectedAreaId) {
            urlSegments.pop();
        }

        if (this.env.IS_NEW_AFC && areaId === 'productCategory:region') {
            const journeyPlannerUrl = [this.translate.currentLang, 'journey-planner'];
            await this.router.navigate(journeyPlannerUrl);
            return null;
        }

        this.selectedAreaId = areaId;
        if (ticket) {
            this.reselectTicketForRepurchaseFlow(ticket);
        } else {
            const baseUrl = [this.translate.currentLang, 'purchase-v2', ...urlSegments, this.selectedAreaId];
            await this.router.navigate(baseUrl);
        }

        this.nextStep.emit();
    }

    private reselectTicketForRepurchaseFlow(
        ticket?: EnhancedFareEngineTicket & {
            geography: { zones: Array<{ code: string }> };
            timeIntervals: Array<{ code: string }>;
        }
    ) {
        const isJPticket = this.isJourneyPlannerTicket(ticket);

        if (isJPticket) {
            this.reselectJPTicketForRepurchaseFlow(ticket);
        } else {
            this.reselectRegTicketForRepurchaseFlow(ticket);
        }
    }

    private reselectJPTicketForRepurchaseFlow(
        ticket?: EnhancedFareEngineTicket & {
            geography: { zones: Array<{ code: string }> };
            timeIntervals: Array<{ code: string }>;
        }
    ) {
        // Handle re-purchase for Journey Planner tickets by rebuilding the original search context.
        const { geography, dynamicProductParameters, startZone, endZone, startZoneName, endZoneName } = ticket;

        // Collect all zone codes and derive helper flags/values for origin, destination and pricing.
        const zones = geography?.zones?.map((zone) => zone.code).filter(Boolean) ?? [];
        const hasMultipleZones = zones.length > 1;
        const priceZoneList = dynamicProductParameters?.priceZoneList || zones.join(',');
        const origin =
            startZone ||
            (zones[0]
                ? {
                      code: zones[0],
                      name:
                          startZoneName ||
                          (geography?.zones?.[0]?.name as any)?.translations?.[0]?.translation ||
                          zones[0]
                  }
                : null);
        const destination =
            endZone ||
            (hasMultipleZones ? { code: zones[zones.length - 1], name: endZoneName || zones[zones.length - 1] } : null);

        // Build the query parameters expected by Journey Planner so it can prefill the search state.
        const journeyParams = new URLSearchParams({
            origin: JSON.stringify(origin ?? null),
            destination: JSON.stringify(destination ?? null),
            itinerary: JSON.stringify(null),
            searchDateAndTime: JSON.stringify(null),
            duration: JSON.stringify(dynamicProductParameters?.duration ?? 0),
            priceZoneList: JSON.stringify(priceZoneList)
        });

        // Persist Journey Planner query params and ticket metadata in session for the next JP screen.
        this.sessionService.setJourneyQueryParams(journeyParams.toString());
        this.sessionService.setJourneyRepurchaseData({
            productId: ticket.productId,
            productParametersCode: ticket.productParametersCode,
            dynamicProductParameters: ticket.dynamicProductParameters,
            startZoneId: ticket.startZoneId,
            endZoneId: ticket.endZoneId,
            activations: ticket?.activations
        });

        // Redirect the user back into the Journey Planner purchase flow with the restored context.
        const ticketUrlSegments = [this.translate.currentLang, 'journey-planner', 'purchase', 'category-product'];

        setTimeout(() => {
            this.router.navigate(ticketUrlSegments);
        }, 0);
    }

    private reselectRegTicketForRepurchaseFlow(
        ticket?: EnhancedFareEngineTicket & {
            geography: { zones: Array<{ code: string }> };
            timeIntervals: Array<{ code: string }>;
        }
    ) {
        const { typeOfProduct, geography, timeIntervals, productId, dynamicProductParameters } = ticket;

        const productCategory = typeOfProduct.code === 'single' ? 'city-muni' : 'area';
        const zone = geography.zones[0].code;
        const productType =
            typeOfProduct.code === 'single' ? 'productType:single' : `timeInterval:${timeIntervals[0].code}`;

        const productSegments: string[] = [];

        const hasDynamicParams = dynamicProductParameters && Object.keys(dynamicProductParameters).length > 0;

        if (hasDynamicParams) {
            Object.entries(dynamicProductParameters).forEach(([key, value]) => {
                productSegments.push(`${productId}:${key}`, `${value}`);
            });
        } else {
            productSegments.push(`${productId}`, '1');
        }
        const ticketUrlSegments = [
            this.translate.currentLang,
            'purchase-v2',
            'areas',
            `productCategory:${productCategory}`,
            'product-area',
            `zone:${zone}`,
            'category-product',
            `${productType}`,
            'product',
            ...productSegments,
            'ticket-activation',
            'AFTER_PURCHASE'
        ];
        if (ticket) {
            ticketUrlSegments.push('recent');
        }

        setTimeout(() => {
            this.router.navigate(ticketUrlSegments);
        }, 0);
    }

    private initSubscriptions(): void {
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });

        if (this.env.IS_NEW_AFC) {
            // Trigger the recent purchases request whenever the customer account changes.
            this.customerAccountsFacade.selectedAccount$
                .pipe(
                    takeUntil(this.componentDestroyed$),
                    switchMap((selectedAccount: CustomerAccount | null) => {
                        const accountId = selectedAccount?.id;
                        return this.ticketService.getRecentTickets(accountId);
                    })
                )
                .subscribe((data: EnhancedFareEngineTicket[]) => {
                    this.recentTickets = data;
                });
        }
    }

    private isJourneyPlannerTicket(
        ticket?: EnhancedFareEngineTicket & {
            geography: { zones: Array<{ code: string }> };
            timeIntervals: Array<{ code: string }>;
        }
    ): boolean {
        const isJPlannerTicket = !ticket?.productCode?.includes('FareZone');
        return isJPlannerTicket;
    }
}
